import React, {
  useContext,
  useEffect,
  useState
} from 'react';
import ReactMarkdown from 'react-markdown';
import { ELSIcon } from '@els/els-component-form-field-react';
import PageHeader from '../../components/page-header/PageHeader.component';
import { useHistory } from 'react-router';
import {
  ELSPropsFromToastService,
  ELSWithToastService
} from '@els/els-component-toast-react';
import { flowRight, get } from 'lodash';
import withHTMLHeadSEO from '../../hocs/with-html-head-seo/withHTMLHeadSEO.hoc';
import { FlexLayoutModifier } from '../../components/flex/flex.constants';
import IsRender from '../../components/is-render/IsRender.component';
import { FlexItem } from '../../components/flex/FlexItem.component';
import { FlexLayout } from '../../components/flex/FlexLayout.component';
import { RoutePath } from '../../components/app/app.constants';
import { addSearchParams } from '../../utilities/app.utilities';
import { osmosisVideoUrl } from './osmosis-player.constants';
import withRecommendationPlayer, {
  RecommendationPlayerProps,
} from '../../hocs/with-recommendation-player/withRecommendationPlayer.hoc';
import withRemediationBase from '../../hocs/with-remediation-base/withRemediationBase.hoc';
import {
  fetchOsmosisLearnDataAction,
  fetchOsmosisTokenAction,
} from '../../components/app/app-state-actions.utilities';
import { AppContext } from '../../components/app/AppProvider.component';
import {
  getAssessmentGoalFromRecommendation,
  getMinutesAndSecondsFromSeconds,
  getRecommendationContentId,
  getRecommendationTitle,
  getTotalProgress
} from '../../utilities/remediation.utilities';
import { ELSTooltip } from '@els/els-component-tooltip-react';
import { ELSButton } from '@els/els-component-button-react';
import { RecommendationAttemptStatusDto } from '../../apis/nhe-remediation-service/nhe-remediation-service.dtos';
import {
  ELSPropsFromModalService,
  ELSWithModalService
} from '@els/els-component-modal-react';
import { SherpathAIChatSidebar } from '../../components/sherpath-ai-chat-sidebar/SherpathAIChatSidebar.component';
import { SherpathAIChatToggle } from '../../components/sherpath-ai-chat-toggle/SherpathAIChatToggle.component';
import cx from 'classnames';
import { getBooleanFromGroupFeatureFlagWithFallbackToGlobal } from '../../utilities/featureFlag.utilities';
import { FEATURE_FLAG } from '../../apis/eols-features-api/eols-features-api.constants';
import {
  PageContextType,
  buildSherpathAIPageContext
} from '../../utilities/sherpath-ai-context.utilities';
import { getIsAIChatPageContextEnabled } from '../../components/app/app-state-selectors.utilities';
import { buildOsmosisVideoPageContext } from '../../utilities/sherpath-ai-page-context-builders.utilities';
import CloseRecommendationPlayerModal, { CloseRecommendationPlayerModalId } from '../../components/close-recommendation-player-modal/CloseRecommendationPlayerModal.component';
import FocusTrap from 'focus-trap-react';

export type OsmosisPlayerPropsOnly = Record<string, unknown>;

export interface OsmosisPlayerState {
  transcript: string;
  isSidebarExpanded: boolean;
  intervalId: NodeJS.Timeout;
  secondsSinceStart: number;
  videoCompleted: boolean;
}

const defaultState: OsmosisPlayerState = {
  transcript: null,
  isSidebarExpanded: false,
  intervalId: null,
  secondsSinceStart: 0,
  videoCompleted: false
};

export type OsmosisPlayerPageProps = RecommendationPlayerProps
  & ELSPropsFromToastService
  & ELSPropsFromModalService
  & OsmosisPlayerPropsOnly

// eslint-disable-next-line sonarjs/cognitive-complexity
export const OsmosisPlayerPageComponent = (props: OsmosisPlayerPageProps): JSX.Element => {

  const [state, setState] = useState<OsmosisPlayerState>(defaultState);
  const {
    appState,
    setAppState,
    appStateRef
  } = useContext(AppContext);
  const history = useHistory();

  useEffect(() => {
    return () => {
      if (state.intervalId) {
        clearInterval(state.intervalId);
      }
    };
  }, [state.intervalId]);

  const startTimer = () => {
    const intervalId = setInterval(() => {
      setState((prevState) => {
        const newTime = prevState.secondsSinceStart + 1;
        return {
          ...prevState,
          secondsSinceStart: newTime,
        };
      });
    }, 1000);

    setState((prevState) => ({
      ...prevState,
      intervalId,
    }));

  }

  const doesRecBelongToUser = (): boolean => {
    if (!props.recommendationAttempt) {
      return false;
    }

    return props.recommendation.userId === Number(appState.userId);
  };

  const osmosisEventListener = (e) => {
    if (e.origin !== osmosisVideoUrl) {
      return;
    }

    if (!e.data) {
      return;
    }

    if (!e.data.type) {
      return;
    }

    if (
      props.referer === RoutePath.REMEDIATION_STUDENT_DETAIL
      || !doesRecBelongToUser()
    ) {
      return;
    }

    if (e.data.type === 'video_start') {
      startTimer();
      return;
    }

    if (
      e.data.type === 'video_finished'
    ) {
      setState((prevState) => {
        return {
          ...prevState,
          videoCompleted: true
        }
      });
    }

  };

  useEffect(() => {
    const contentId = getRecommendationContentId(props.recommendation);
    if (!contentId) {
      return;
    }
    fetchOsmosisTokenAction({
      setAppState,
      appState,
      appStateRef
    });
    fetchOsmosisLearnDataAction({ setAppState, appState, appStateRef, learnNodeId: contentId })
      .then((_response) => {
        setState((prevState) => {
          return {
            ...prevState,
            transcript: get(_response, 'sections.transcripts[0].data.content'),
          }
        });
      });
  }, [props.recommendation]);

  useEffect(() => {
    window.addEventListener('message', osmosisEventListener, false);
    return () => {
      window.removeEventListener('message', osmosisEventListener, false);
    };
  }, [props.recommendationAttempt]);

  const getPageContext = (): string => {
    if (!props.recommendation || !appState.remediationBaseState.remediationAssignmentWithSources) {
      return null;
    }

    if (!getIsAIChatPageContextEnabled(appState)) {
      return null;
    }

    // Build full context with video-specific data
    return buildSherpathAIPageContext({
      remediationAssignmentWithSources: appState.remediationBaseState.remediationAssignmentWithSources,
      assessmentMetrics: appState.remediationBaseState.assessmentMetrics,
      assessmentSources: appState.remediationBaseState.raasAssessmentSourceList,
      hesiExamInfo: appState.remediationBaseState.hesiExamInfo,
      totalProgressPercent: getTotalProgress(appState.remediationBaseState),
      pageType: PageContextType.OSMOSIS_VIDEO,
      pageSpecificData: buildOsmosisVideoPageContext({
        recommendation: props.recommendation,
        assessmentGoal: appState.remediationBaseState.eolsAssessment
          ? getAssessmentGoalFromRecommendation(appState.remediationBaseState.eolsAssessment, props.recommendation)
          : null,
        transcript: state.transcript
      }),
    });
  };

  const handleReturnNavigation = () => {
    if (props.referer === RoutePath.REMEDIATION_STUDENT_HOME) {
      return history.push(addSearchParams(RoutePath.REMEDIATION_POST_PLAYER, {
        recommendationAttemptId: props.recommendationAttempt.id
      }));
    }

    if (props.referer === RoutePath.REMEDIATION_STUDENT_DETAIL) {
      return history.push(RoutePath.REMEDIATION_STUDENT_DETAIL);
    }

    return null;
  };

  const handleSidebarToggle = () => {
    setState((prevState) => {
      return {
        ...prevState,
        isSidebarExpanded: !state.isSidebarExpanded
      }
    })
  };

  const {
    recommendation,
  } = props;

  if (!appState.osmosisTokenDto || !recommendation) {
    return null;
  }

  const {
    transcript,
    isSidebarExpanded
  } = state;

  const sideBarClasses = ['c-scm-osmosis-player__sidebar'];

  if (isSidebarExpanded) {
    sideBarClasses.push('c-scm-osmosis-player__sidebar--expanded');
  }

  const sidebarControlsIcon = isSidebarExpanded ? 'nav-collapse' : 'nav-expand';
  const contentId = getRecommendationContentId(recommendation);

  const showCompleteButton = () => {
    return props.referer === RoutePath.REMEDIATION_STUDENT_HOME
      && props.recommendationAttempt
      && props.recommendationAttempt.status !== RecommendationAttemptStatusDto.COMPLETED_PASSED;
  };

  const convertDurationToSeconds = (duration: string): number => {
    // example video estimatedTime = PT14M48S
    const regex = /PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/;
    const matches = duration.match(regex);

    if (!matches) {
      return 0;
    }

    const hours = parseInt(matches[1] || '0', 10);
    const minutes = parseInt(matches[2] || '0', 10);
    const seconds = parseInt(matches[3] || '0', 10);

    return hours * 3600 + minutes * 60 + seconds;
  }

  const isCompleteBtnDisabled = (): boolean => {

    // Check feature flag to disable player timeout for QE testing
    const isTimeoutDisabled = getBooleanFromGroupFeatureFlagWithFallbackToGlobal(
      appState.featureFlagsGrouped,
      FEATURE_FLAG.DISABLE_PLAYER_TIMEOUTS,
      appState.courseSectionId
    );

    if (isTimeoutDisabled) {
      return false;
    }

    if (!state.videoCompleted) {
      return true;
    }
    if (!props.recommendation?.estimatedTime) {
      return false;
    }

    const totalVideoSeconds = convertDurationToSeconds(props.recommendation.estimatedTime);
    const timeThresholdPercentage = 0.5;
    const videoSecondsThreshold = Math.floor(totalVideoSeconds * timeThresholdPercentage);
    return state.secondsSinceStart < videoSecondsThreshold;

  }

  const getToolTipContent = () => {
    if (isCompleteBtnDisabled()) {
      return (
        <>
          Video not opened long enough to mark as completed. Total watch time: {getMinutesAndSecondsFromSeconds(state.secondsSinceStart)}
        </>
      );
    }
    return (
      <>
        Click here if you have completed your video to mark as completed.
      </>
    );
  };

  const openPlayerModal = (isForSubmission: boolean, onConfirm: () => void) => {
    props.modalService.openCustomModal({
      modalId: CloseRecommendationPlayerModalId,
      noFocusTrap: true,
      modal: (
        <FocusTrap>
          <CloseRecommendationPlayerModal
              modalService={props.modalService}
              recommendationId={recommendation.id}
              recommendationFeedback={props.recommendationFeedback}
              recommendationContentType={recommendation.contentType}
              isForSubmission={isForSubmission}
              onConfirm={onConfirm}
          />
        </FocusTrap>
      ),
    });
  };

  const handleOpenCompleteModal = () => {
    if (isCompleteBtnDisabled()) {
      return;
    }
    openPlayerModal(true, () => {
      props.handleSubmitAttempt(state.secondsSinceStart).then(() => {
        clearInterval(state.intervalId);
        handleReturnNavigation();
      });
    });
  };

  return (
    <div className="c-scm-osmosis-player">
      <header className="c-scm-osmosis-player__header">
        <PageHeader
          onCloseClick={() => openPlayerModal(false, handleReturnNavigation)}
          title="Osmosis Video"
        >
          <SherpathAIChatToggle
            remediationAssignmentWithSources={appState.remediationBaseState.remediationAssignmentWithSources}
            pageContextType={PageContextType.OSMOSIS_VIDEO}
          />
        </PageHeader>
      </header>

      <div className="c-scm-osmosis-player__main">
        <div className={cx('c-scm-osmosis-player__content', {
          'c-scm-osmosis-player__content--chat-active': appState.isChatActive
        })}>
          <div className="c-scm-osmosis-player__body-wrap">
            <FlexLayout modifiers={[FlexLayoutModifier.WRAP_AT_MOBILE]} classes={['c-scm-osmosis-player__body']}>
              <FlexItem
                isRender={!!transcript}
                tag="aside"
                classes={sideBarClasses}
              >
                <div className="c-scm-osmosis-player__sidebar-content">
                  <h4 className="u-els-margin-bottom">Transcript</h4>
                  <ReactMarkdown>
                    {transcript}
                  </ReactMarkdown>
                </div>
              </FlexItem>
              <FlexItem modifiers={[FlexLayoutModifier.GROW]}
                        classes={['c-scm-osmosis-player__page']}>

                <div className="c-scm-osmosis-player__sidebar-controls">
                  <FlexLayout modifiers={[FlexLayoutModifier.SPACE_BETWEEN, FlexLayoutModifier.MIDDLE]}>
                    <FlexItem isRender={!!transcript}>
                      <button
                        type="button"
                        className="u-els-debuttonize u-els-anchorize"
                        onClick={handleSidebarToggle}
                      >
                        <FlexLayout modifiers={[FlexLayoutModifier.MIDDLE, FlexLayoutModifier.GUTTERS_1o2]}>
                          <FlexItem>
                            <ELSIcon
                              prefix="hmds"
                              name={sidebarControlsIcon}
                              align="middle"
                              size="1x1o2"
                              color="secondary"
                              aria-label="test"
                            />
                          </FlexItem>
                          <FlexItem>
                            {isSidebarExpanded ? 'Close Transcript' : 'Open Transcript'}
                          </FlexItem>
                        </FlexLayout>
                      </button>
                    </FlexItem>
                  </FlexLayout>
                </div>

                {
                  contentId && (
                    <iframe
                      title="osmosis-video-iframe"
                      src={`${osmosisVideoUrl}/embed/${encodeURIComponent(contentId)}?auth=${appState.osmosisTokenDto.authToken}`}
                      className="c-scm-osmosis-player__iframe"
                      allowFullScreen />
                  )
                }
                <IsRender isRender={!!transcript}>
                  <div className="c-scm-osmosis-player__sidebar-content-mobile">
                    <h4 className="u-els-margin-bottom">Transcript</h4>
                    <ReactMarkdown>
                      {transcript}
                    </ReactMarkdown>
                  </div>
                </IsRender>
              </FlexItem>
            </FlexLayout>
            <SherpathAIChatSidebar
              remediationAssignmentWithSources={appState.remediationBaseState.remediationAssignmentWithSources}
              pageContext={getPageContext()}
              pageContextType={PageContextType.OSMOSIS_VIDEO}
            />
          </div>

          <div className="c-scm-osmosis-player__footer">
            <FlexLayout modifiers={[FlexLayoutModifier.GUTTERS_2X, FlexLayoutModifier.MIDDLE]}>
              <FlexItem modifiers={[FlexLayoutModifier.GROW]}>
                <h4>{getRecommendationTitle(recommendation)}</h4>
              </FlexItem>
              <FlexItem isRender={showCompleteButton()}>
                <ELSTooltip
                  showWhenHover
                  content={getToolTipContent()}
                >
                  <ELSButton
                    isDisabled={isCompleteBtnDisabled()}
                    onClick={handleOpenCompleteModal}
                    size="small"
                  >
                    Complete video
                  </ELSButton>
                </ELSTooltip>

              </FlexItem>
            </FlexLayout>
          </div>
        </div>
      </div>
    </div>
  );
};

// Always refetch recommendation attempts/sources/metrics/submissions on mount
// (rather than relying on cache) so a newly created attempt from launching this
// player is picked up, since postRecommendationAttemptAction no longer writes it
// into state itself. Matches EbookPlayer.page.tsx's existing behavior.
const updateProgressState = false;

const enhance = flowRight(
  withHTMLHeadSEO({ title: 'Osmosis Player' }),
  ELSWithToastService,
  ELSWithModalService,
  withRemediationBase(updateProgressState),
  withRecommendationPlayer,
);

const OsmosisPlayerPage = enhance(OsmosisPlayerPageComponent) as unknown as React.FC<OsmosisPlayerPropsOnly>;

export default OsmosisPlayerPage;
